import {
  ApplicationType,
  DirectionEnum,
  isTranslatableItem,
  type DrawerType,
  type ICoordinate,
  type IDrawer,
  type IMapPlaceDto,
  type IPoint,
  type IStationDto,
  type RydeUiUnit,
  type SimpleResult,
} from "@ryde/shared";

import {
  MAPLIBRE_PLACE_INFO,
  MAPLIBRE_PLACE_INFO_STATION_ENTITY,
  MAPLIBRE_PLACE_INFO_STATUS,
} from "./maplibre-place-info.constants";
import type {
  IMapLibrePlaceInfoDisplay,
  IMapLibrePlaceInfoLookup,
} from "./maplibre-place-info.models";

/**
 * Whether a press and its release are one click, as MapLibre itself decides: a release with no press
 * seen on the map (it started on a marker or a control) is not a click on the map.
 */
export const isWithinClickTolerance = ({
  pressPoint,
  releasePoint,
}: {
  pressPoint: IPoint | undefined;
  releasePoint: IPoint;
}): boolean =>
  !!pressPoint &&
  Math.hypot(releasePoint.x - pressPoint.x, releasePoint.y - pressPoint.y) <=
    MAPLIBRE_PLACE_INFO.clickTolerancePx;

/**
 * The lookup state a finished request leaves: `null` data is no address. A failure keeps the server's
 * answer when it carries a translation, for the popup to show; no response at all fails without one.
 */
export const toPlaceInfoLookup = ({
  coordinate,
  result,
}: {
  coordinate: ICoordinate;
  result: SimpleResult<IMapPlaceDto | null> | undefined;
}): IMapLibrePlaceInfoLookup => {
  if (!result?.success) {
    return {
      coordinate,
      status: MAPLIBRE_PLACE_INFO_STATUS.Failed,
      ...(isTranslatableItem(result?.error) ? { failure: result } : {}),
    } satisfies IMapLibrePlaceInfoLookup;
  }

  if (!result.data) {
    return {
      coordinate,
      status: MAPLIBRE_PLACE_INFO_STATUS.Empty,
    } satisfies IMapLibrePlaceInfoLookup;
  }

  return {
    coordinate,
    status: MAPLIBRE_PLACE_INFO_STATUS.Found,
    place: result.data,
  } satisfies IMapLibrePlaceInfoLookup;
};

/** Retry is offered only when no answer came back; a failure the server explained would come back the same. */
export const canRetryPlaceInfoLookup = ({
  status,
  failure,
}: IMapLibrePlaceInfoLookup): boolean =>
  status === MAPLIBRE_PLACE_INFO_STATUS.Failed && !failure;

export const toPlaceInfoDisplay = (
  place: IMapPlaceDto,
): IMapLibrePlaceInfoDisplay =>
  place.name
    ? ({
        title: place.name,
        address: place.address,
      } satisfies IMapLibrePlaceInfoDisplay)
    : ({ title: place.address } satisfies IMapLibrePlaceInfoDisplay);

/** `lat, lng` to about a metre, the form people paste into other maps. */
export const formatPlaceInfoCoordinate = ({ lat, lng }: ICoordinate): string =>
  [lat, lng]
    .map(value => value.toFixed(MAPLIBRE_PLACE_INFO.coordinateFractionDigits))
    .join(", ");

/**
 * The new station the drawer opens with: named after the place (or its address), sitting exactly where
 * the user clicked rather than where the geocoder centres the place. Same defaults as the passengers
 * map's add-station flow — an official, active stop for both directions.
 */
export const buildPlaceInfoStationDraft = ({
  coordinate,
  place,
}: {
  coordinate: ICoordinate;
  place: IMapPlaceDto | undefined;
}): Partial<IStationDto> => {
  const draft: Partial<IStationDto> = {
    name: place?.name ?? place?.address,
    direction: DirectionEnum.Both,
    metadata: {
      isActive: true,
      isOfficial: true,
      locationInfo: {
        latitude: coordinate.lat,
        longitude: coordinate.lng,
        fullAddress: place?.address,
      },
    },
  };

  return draft;
};

/** Whether a drawer for the entity is already open, which owns the edit-form slot a new one would reuse. */
export const isEntityDrawerOpen = ({
  drawers,
  entity,
}: {
  drawers: IDrawer<DrawerType>[];
  entity: RydeUiUnit;
}): boolean =>
  drawers.some(
    ({ drawerProps }) =>
      "entity" in drawerProps && drawerProps.entity === entity,
  );

/**
 * Whether the popup offers "Add station here": in the regular app only (never admin or a landing page), to
 * a user who may create stations, not while a station drawer is open — the new one would overwrite it —
 * and not while a full-screen overlay covers the drawers, where the new one would open unseen.
 */
export const canOfferPlaceInfoStation = ({
  applicationType,
  canCreateStation,
  drawers,
  areDrawersCovered,
}: {
  applicationType: ApplicationType;
  canCreateStation: boolean;
  drawers: IDrawer<DrawerType>[];
  areDrawersCovered: boolean;
}): boolean =>
  applicationType === ApplicationType.Main &&
  canCreateStation &&
  !areDrawersCovered &&
  !isEntityDrawerOpen({ drawers, entity: MAPLIBRE_PLACE_INFO_STATION_ENTITY });
